/**
 * Banco de preguntas reutilizables.
 *
 * El banco lo forman las preguntas SIN instrumento. Asignar una pregunta del
 * banco a un indicador la registra en el pivote M:N `indicador_pregunta`, que es
 * CATÁLOGO (a qué indicadores puede servir) y no calificación.
 *
 * Store Pinia: estado compartido del módulo y orquestación de sus servicios.
 * Las vistas no llaman a `http` ni a los servicios directamente; pasan por aquí.
 * Los servicios no lanzan, así que las acciones interpretan valores neutros
 * (`[]`, `null`, `false`) como fallo.
 */

import { defineStore } from 'pinia'
import type Pregunta from '@/interfaces/Preguntas'
import {
  listarBancoService,
  crearPreguntaBancoService,
  actualizarPreguntaBancoService,
  eliminarPreguntaBancoService,
  asignarAIndicadoresService,
  desasignarDeIndicadorService,
  cargarPreguntasDeIndicadorService,
} from '@/services/BancoPreguntasService'

/** Store `GestionBancoPreguntas`. Ver la cabecera del archivo para el contrato del módulo. */
export const useGestionBancoPreguntas = defineStore('GestionBancoPreguntas', {
  state: () => ({
    preguntasBanco: [] as Pregunta[],
    preguntasDeIndicador: [] as Pregunta[],
    preguntaActual: null as Pregunta | null,
  }),

  actions: {
    async cargarBanco() {
      this.preguntasBanco = await listarBancoService()
    },

    async crearPregunta(payload: {
      pregunta: string
      tipo: string
      id_grupo_interes?: number | null
      opciones?: string[]
    }) {
      const result = await crearPreguntaBancoService(payload)
      if (result.ok && result.data) {
        this.preguntasBanco.push(result.data)
        this.preguntaActual = result.data
      }
      return result
    },

    async actualizarPregunta(id: number, payload: {
      pregunta?: string
      tipo?: string
      id_grupo_interes?: number | null
      opciones?: string[]
    }) {
      const result = await actualizarPreguntaBancoService(id, payload)
      if (result.ok && result.data) {
        const idx = this.preguntasBanco.findIndex(p => p.id_pregunta === id)
        if (idx !== -1) this.preguntasBanco[idx] = result.data
        if (this.preguntaActual?.id_pregunta === id) this.preguntaActual = result.data
      }
      return result
    },

    async eliminarPregunta(id: number) {
      const result = await eliminarPreguntaBancoService(id)
      if (result.ok) {
        this.preguntasBanco = this.preguntasBanco.filter(p => p.id_pregunta !== id)
        if (this.preguntaActual?.id_pregunta === id) this.preguntaActual = null
      }
      return result
    },

    async asignarAIndicadores(id: number, indicadores: number[]) {
      const result = await asignarAIndicadoresService(id, indicadores)
      if (result.ok && result.data) {
        const idx = this.preguntasBanco.findIndex(p => p.id_pregunta === id)
        if (idx !== -1) this.preguntasBanco[idx] = result.data
      }
      return result
    },

    async desasignarDeIndicador(id: number, idIndicador: number) {
      const result = await desasignarDeIndicadorService(id, idIndicador)
      if (result.ok) {
        const pregunta = this.preguntasBanco.find(p => p.id_pregunta === id)
        if (pregunta?.indicadores) {
          pregunta.indicadores = pregunta.indicadores.filter(i => i.id_indicador !== idIndicador)
        }
      }
      return result
    },

    async cargarPreguntasDeIndicador(idIndicador: number) {
      this.preguntasDeIndicador = await cargarPreguntasDeIndicadorService(idIndicador)
    },
  },
})
